import * as Sentry from "@sentry/react";
import { type StaticDecode, type TSchema, Type } from "typebox";
import * as TypeCompiler from "typebox/compile";
import {
  StalePayloadError,
  readJsonScript,
} from "@/shared/bootstrap/bootContract.ts";
import {
  Nullable,
  ReadonlyArray,
  ReadonlyObject,
} from "@/shared/schema/schema.ts";
import type { PassedValidator } from "@/shared/util/decoded.ts";

const ELEMENT_ID = "cvp-login-data";
const RELOADED_AT_KEY = "cvp-login-payload-reload";
// Long enough to cover a reload that has not landed yet, so a hop that recurs on
// every load cannot reload in a loop.
const RELOAD_COOLDOWN_MS = 5000;

// Rails never sees the fragment, so it is not part of the page.
function currentPage() {
  return window.location.pathname + window.location.search;
}

// Evaluated while the page Rails rendered the payload for is booting.
const bootPage = currentPage();
const bootPath = window.location.pathname;

// Rails renders every key on every login page, so each flow validates only the
// keys it needs rather than one schema of everything-optional.
const loginErrorSchema = ReadonlyObject({
  message: Type.String(),
  err_params: Type.Record(Type.String(), Nullable(Type.String())),
  support_email: Nullable(Type.String()),
});

const adminAsSchema = ReadonlyObject({
  users: ReadonlyArray(
    ReadonlyObject({
      id: Type.String(),
      name: Nullable(Type.String()),
      email: Type.String(),
    }),
  ),
});

const unknownUserSchema = ReadonlyObject({
  field: Type.String(),
  user_id: Type.String(),
  support_email: Nullable(Type.String()),
  federation_group_members: ReadonlyArray(
    ReadonlyObject({ subdomains: ReadonlyArray(Type.String()) }),
  ),
});

const domainSelectionSchema = ReadonlyObject({
  google_apps_domains: ReadonlyArray(ReadonlyObject({ domain: Type.String() })),
});

const passwordSchema = ReadonlyObject({
  user_id: Nullable(Type.String()),
  reset_password_code: Nullable(Type.String()),
});

const loginErrorChecker = TypeCompiler.Compile(loginErrorSchema);
const adminAsChecker = TypeCompiler.Compile(adminAsSchema);
const unknownUserChecker = TypeCompiler.Compile(unknownUserSchema);
const domainSelectionChecker = TypeCompiler.Compile(domainSelectionSchema);
const passwordChecker = TypeCompiler.Compile(passwordSchema);

function reloadedAt(): number {
  try {
    return Number(sessionStorage.getItem(RELOADED_AT_KEY)) || 0;
  } catch {
    return 0;
  }
}

// False when storage is blocked: a reload that cannot be counted must not happen,
// or a hop that recurs on every load would reload forever.
function rememberReloadedAt(now: number): boolean {
  try {
    sessionStorage.setItem(RELOADED_AT_KEY, String(now));

    return true;
  } catch {
    return false;
  }
}

// The payload is for the URL the page booted at. Once the URL has moved it
// describes another page, so reload rather than decode. When a reload is not
// due, the error page with its refresh button is the fallback.
export function readLoginData<T extends TSchema>(
  checker: PassedValidator<T>,
  reload = () => window.location.reload(),
): StaticDecode<T> {
  const page = currentPage();

  if (page === bootPage) {
    return readJsonScript(ELEMENT_ID, checker);
  }

  // Paths only: a reset link carries its code in the query.
  const description = `Stale login payload: booted at ${bootPath}, read at ${window.location.pathname}`;
  const now = Date.now();

  if (now - reloadedAt() <= RELOAD_COOLDOWN_MS || !rememberReloadedAt(now)) {
    throw new Error(description);
  }

  const error = new StalePayloadError(description);

  Sentry.withScope((scope) => {
    scope.setLevel("warning");
    scope.captureException(error);
  });
  reload();

  throw error;
}

export const readLoginErrorData = () => readLoginData(loginErrorChecker);

export const readAdminAsData = () => readLoginData(adminAsChecker);

export const readUnknownUserData = () => readLoginData(unknownUserChecker);

export const readDomainSelectionData = () =>
  readLoginData(domainSelectionChecker);

export const readPasswordData = () => readLoginData(passwordChecker);
